/**
 * One place for the curve palette and unit labels.
 *
 * The colours used to be hex literals scattered through `LogViewer`'s track
 * builder, where a collision was invisible: FZI and the per-rock-type
 * saturation were both `#8e24aa`, which nobody could see while each lived
 * inside its own chart. Once the headers show a colour chip per curve, side by
 * side, the same purple twice reads as a bug.
 *
 * Kept deliberately literal rather than mapped onto `--ds-*` tokens: these are
 * *data* colours that have to stay distinguishable from each other, not chrome
 * colours that should follow the theme. Track chrome uses the tokens.
 */

export const CURVE_COLORS = {
  rqi: '#00838f',
  fzi: '#6a1b9a',
  /** The tendency-fit saturation. */
  swModel: '#1e88e5',
  /**
   * The per-rock-type saturation. Amber rather than the purple it shared with
   * FZI -- the two now appear as chips in adjacent headers.
   */
  swModelByRockType: '#f9a825',
  /** Measured log saturation. */
  swMeasured: '#e53935',
  /** Measured core-plug saturation, drawn as points. */
  swCore: '#00897b',
  /** Project sample markers, on every index track. */
  sample: '#ff6f00',
  /**
   * The rock-type track's header accent. Neutral on purpose: that track's
   * "colour" is the *set* of FZI-bin colours, which the band key states, so a
   * single hue would misrepresent it.
   */
  rockType: '#78909c'
} as const;

/**
 * Units per curve.
 *
 * Hardcoded because nothing carries unit metadata this far: the Excel importer
 * does resolve a units map, but it lands on `WellLogImportPreview.units` and is
 * never persisted onto `WellLogs`. Follow-up worth doing -- persist those units
 * and read them here -- at which point this map becomes the fallback.
 */
export const CURVE_UNITS: Record<string, string> = {
  rqi: 'µm',
  fzi: 'µm',
  sw: 'frac',
  depth: 'ft'
};
